import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';
import { cssPercentage } from '../../../core';
import { UserAvatar } from '../../layout/userAvatar';
import {
  PlayerItem,
  RedactionMomentType,
  RedactionMomentTypes,
} from '../../../entities/player/playerItem';
import { MarkerBarState } from './markerBarState';
import { PlayerReactionIcon } from '../../playerReactions/reactionIcon';
import { useEffect } from 'react';
import { ComponentVisibility, IComponentPolicy } from '../../componentSchema';
import { PlayerComponentName } from '../playerSchema';
import { Moment } from '../../../entities';

const REDACTION_ICON_MAP: Record<RedactionMomentType, string> = {
  FaceBlur: '🎭',
  AudioMuted: '🔇',
  ScreenBlur: '🖥️',
  LicenseBlur: '🚗',
  BlankScreen: '⬛',
};

const isRedactionMomentType = (momentType: string) =>
  !!momentType &&
  Object.values(RedactionMomentTypes).includes(
    momentType as RedactionMomentType
  );

type RedactionMarkerItemProps = {
  model: MarkerBarState;
  moment: Moment;
};

const RedactionMarkerItem = observer(
  ({ model, moment }: RedactionMarkerItemProps) => {
    const { player } = model;

    const startRatio = player.getTimeRatio(moment.startTime);
    const endRatio = player.getTimeRatio(moment.endTime);

    if (startRatio == null) return null;
    const safeEndRatio = endRatio ?? startRatio;

    const midRatio = (startRatio + safeEndRatio) / 2;
    const durationPct = Math.max(0, (safeEndRatio - startRatio) * 100);

    const icon = REDACTION_ICON_MAP[moment.momentType as RedactionMomentType];
    const activeTarget = player.momentView.activeRedactionMoment;

    const className = classNames('marker-item redaction-item', {
      active: activeTarget?.id === moment.id,
    });

    return (
      <li
        key={moment.id}
        className={className}
        style={{
          left: cssPercentage(midRatio),
          ['--redaction-duration' as string]: `${durationPct}%`,
        }}
        onClick={() =>
          player.invoke('jumpToMoment', { moment, setAsPrincipal: false })
        }
      >
        <span className="marker-reveal redaction-icon">{icon}</span>
      </li>
    );
  }
);

type Props = {
  model: MarkerBarState;
  policy?: IComponentPolicy;
};

export const PlayerMarkerBar = observer(
  ({
    model,
    policy = model.player.getComponentPolicy(PlayerComponentName.MarkerBar),
  }: Props) => {
    const { player } = model;

    const [
      indexOfTheLastAddedReaction,
      setIndexOfTheLastAddedReaction,
    ] = useState(-1);
    const items = player.itemSource.markerBarItems;
    const lastAddedReaction = player.itemSource.lastAddedReaction;
    const activeTarget = player.momentView.activeCommentOrReaction;

    useEffect(() => {
      let timeout: NodeJS.Timeout;
      if (lastAddedReaction) {
        const latestAddedReactionItem = items.find(
          (item) => item.reactionId === lastAddedReaction.reactionId
        );

        if (latestAddedReactionItem) {
          const indexOfLatestAddedReaction = items.indexOf(
            latestAddedReactionItem
          );
          setIndexOfTheLastAddedReaction(indexOfLatestAddedReaction);

          timeout = setTimeout(() => setIndexOfTheLastAddedReaction(-1), 6000);
        }
      } else {
        setIndexOfTheLastAddedReaction(-1);
      }

      return () => clearTimeout(timeout);
    }, [items, lastAddedReaction]);

    if (policy.visibility !== ComponentVisibility.Visible) return null;

    const className = classNames({
      'show-comment-items': player.frameset?.isSectionVisible('Comments'),
      'show-reaction-items': true,
    });

    return (
      <div id="user-player-marker-bar" className={className}>
        <ul className="marker-list">
          {items.map((item, index) => {
            if (item.moment && isRedactionMomentType(item.moment.momentType)) {
              return (
                <RedactionMarkerItem
                  key={item.id}
                  model={model}
                  moment={item.moment}
                />
              );
            }

            return (
              <MarkerItem
                isLastAddedReaction={index === indexOfTheLastAddedReaction}
                key={item.id}
                isActive={activeTarget?.id === item?.targetId}
                model={model}
                item={item}
              />
            );
          })}
        </ul>
      </div>
    );
  }
);

type MarkerItemProps = {
  model: MarkerBarState;
  item: PlayerItem;
  isActive: boolean;
  isLastAddedReaction: boolean;
};

const MarkerItem = observer(
  ({ model, item, isActive, isLastAddedReaction }: MarkerItemProps) => {
    const { player } = model;
    const target = item?.comment || item?.reaction;
    if (!target) return null;

    const { reaction, watchMode } = target;
    const markerPosition = model.getPosition(target);

    if (!markerPosition) return null; //including 0 for the moment

    const isHovered = model.hoveredCommentId === target.id;

    const className = classNames('marker-item', {
      hovered: isHovered,
      active: isActive && !isLastAddedReaction,
      'reaction-item': target.isReaction,
      'comment-item': target.isComment && !target.isReply,
    });

    let revealElem: React.ReactNode;
    let markerElem: React.ReactNode;

    if (reaction) {
      revealElem = (
        <PlayerReactionIcon
          className={`${isLastAddedReaction ? 'mask-icon' : ''} marker-reveal`}
          reactionName={reaction}
          watchMode={watchMode}
        />
      );
      markerElem = <div className="marker reaction-marker" />;
    } else {
      revealElem = (
        <UserAvatar className="marker-reveal" model={target.actualUser} />
      );
      markerElem = <div className="marker comment-marker" />;
    }

    return (
      <li
        key={target.id}
        className={className}
        onClick={() => player?.invoke('jumpToCommentOrReaction', { target })}
        onMouseEnter={() => model.setHovered(target.id)}
        onMouseLeave={() => model.removeHover()}
        style={{ left: cssPercentage(markerPosition) }}
      >
        {markerElem}
        {revealElem}
      </li>
    );
  }
);
